/**
 * Centralized residency utilities for consistent residency value handling
 * This ensures all residency values are normalized to the standard enum format
 */


/**
 * Standard residency values used throughout the system
 */
export const RESIDENCY_VALUES = {
  RESIDENCE: 'Residence' as const,
  NON_RESIDENCE: 'Non-Residence' as const,
} as const;

/**
 * Display labels for residency values
 */
export const RESIDENCY_LABELS = {
  [RESIDENCY_VALUES.RESIDENCE]: 'Residence',
  [RESIDENCY_VALUES.NON_RESIDENCE]: 'Non-Residence',
} as const;

/**
 * All possible residency value variations that might be encountered
 */
const RESIDENCY_VARIATIONS = {
  residence: [
    'residence',
    'resident', 
    'Residence',
    'Resident',
    'RESIDENCE',
    'RESIDENT'
  ] as const,
  non_residence: [
    'non_residence',
    'non-residence',
    'nonresident',
    'Non-Residence',
    'Non-Residence',
    'NonResidence',
    'NON_RESIDENCE',
    'NON-RESIDENCE',
    'NONRESIDENCE'
  ] as const
} as const;

/**
 * Normalizes any residency value to the standard enum format
 * @param value - Any residency value that might be encountered
 * @returns Standardized residency value ('Residence' | 'Non-Residence')
 */
export function normalizeResidency(value: string | null | undefined): string {
  if (!value) return '';
  
  const normalized = value.trim().toLowerCase().replace(/[-_\s]/g, '');
  
  // Check residence variations (remove special chars for comparison)
  if (RESIDENCY_VARIATIONS.residence.includes(value as any) || 
      normalized === 'residence' || 
      normalized === 'resident') {
    return RESIDENCY_VALUES.RESIDENCE;
  }
  
  // Check non-residence variations (remove special chars for comparison)
  if (RESIDENCY_VARIATIONS.non_residence.includes(value as any) || 
      normalized === 'nonresidence' || 
      normalized === 'nonresident') {
    return RESIDENCY_VALUES.NON_RESIDENCE;
  }
  
  // Return as-is if it matches standard values
  if (value === RESIDENCY_VALUES.RESIDENCE || value === RESIDENCY_VALUES.NON_RESIDENCE) {
    return value;
  }
  
  // Default fallback - return original value
  return value;
}

/**
 * Database residency format values (lowercase with underscore)
 * These match what the database RPC functions expect
 */
export const DB_RESIDENCY_VALUES = {
  RESIDENCE: 'residence' as const,
  NON_RESIDENCE: 'non_residence' as const,
} as const;

/**
 * Normalizes residency value to database format (lowercase with underscore)
 * @param value - Any residency value
 * @returns Database-compatible residency value ('residence' | 'non_residence')
 */
export function normalizeResidencyForDB(value: string | null | undefined): string {
  if (!value) return DB_RESIDENCY_VALUES.NON_RESIDENCE;
  
  const normalized = value.trim().toLowerCase().replace(/[-_\s]/g, '');
  
  // Check residence variations
  if (normalized === 'residence' || normalized === 'resident') {
    return DB_RESIDENCY_VALUES.RESIDENCE;
  }
  
  // Check non-residence variations
  if (normalized === 'nonresidence' || normalized === 'nonresident') {
    return DB_RESIDENCY_VALUES.NON_RESIDENCE;
  }
  
  // Default to non_residence
  return DB_RESIDENCY_VALUES.NON_RESIDENCE;
}

/**
 * Gets the display label for a residency value
 * @param value - Standardized residency value
 * @returns Display label
 */
export function getResidencyLabel(value: string): string {
  const normalized = normalizeResidency(value);
  return RESIDENCY_LABELS[normalized as keyof typeof RESIDENCY_LABELS] || normalized;
}

/**
 * Validates if a residency value is valid
 * @param value - Residency value to validate
 * @returns True if valid
 */
export function isValidResidency(value: string): boolean {
  const normalized = normalizeResidency(value);
  return normalized === RESIDENCY_VALUES.RESIDENCE || normalized === RESIDENCY_VALUES.NON_RESIDENCE;
}

/**
 * Checks if two residency values are compatible
 * @param employeeResidency - Employee's residency
 * @param groupResidency - Group's residency
 * @returns True if compatible
 */
export function areResidenciesCompatible(employeeResidency: string, groupResidency: string): boolean {
  const normalizedEmployee = normalizeResidency(employeeResidency);
  const normalizedGroup = normalizeResidency(groupResidency);
  return normalizedEmployee === normalizedGroup;
}

/**
 * Gets standardized residency options for dropdowns
 * @returns Array of residency options with value and label
 */
export function getResidencyOptions() {
  return [
    { value: RESIDENCY_VALUES.RESIDENCE, label: RESIDENCY_LABELS[RESIDENCY_VALUES.RESIDENCE] },
    { value: RESIDENCY_VALUES.NON_RESIDENCE, label: RESIDENCY_LABELS[RESIDENCY_VALUES.NON_RESIDENCE] },
  ];
}

/**
 * Gets residency badge variant based on residency type
 * @param residency - Residency value
 * @returns Badge variant
 */
export function getResidencyBadgeVariant(residency: string): 'default' | 'secondary' {
  const normalized = normalizeResidency(residency);
  return normalized === RESIDENCY_VALUES.RESIDENCE ? 'default' : 'secondary';
}

/**
 * Gets residency color classes for styling
 * @param residency - Residency value
 * @returns Color classes
 */
export function getResidencyColorClasses(residency: string): string {
  const normalized = normalizeResidency(residency);
  if (normalized === RESIDENCY_VALUES.RESIDENCE) {
    return 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-950/20 dark:text-blue-300 dark:border-blue-800';
  }
  return 'bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-950/20 dark:text-purple-300 dark:border-purple-800';
}

/**
 * Checks if a residency value represents a resident employee
 * @param residency - Residency value to check
 * @returns True if resident
 */
export function isResident(residency: string): boolean {
  const normalized = normalizeResidency(residency);
  return normalized === RESIDENCY_VALUES.RESIDENCE;
}

/**
 * Gets the display value for residency (alias for getResidencyLabel for backward compatibility)
 * @param residency - Residency value
 * @returns Display value
 */
export function getResidencyDisplayValue(residency: string): string {
  return getResidencyLabel(residency);
}